import _ from "lodash";
import React, { useContext, useEffect, useState } from "react";
import NumberFormat from "react-number-format";
import { Button, ButtonGroup, Card, CardBody, Col, Input, Row, Table } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import { toast } from "react-toastify";
import Select from "react-select";
import { useGetItemClasses } from "../hooks";
import { DesktopContainer } from "../layouts";
import { api, constants, helpers, UserContext } from "../utils";
import {
  ItemDropdownMenu,
  ItemTypeahead,
  Pager,
  PORawMaterialDropDown,
  ServiceThreadSpinner,
  VendorTypeahead,
} from "./";

export default function PurchaseOrderPlanning(props) {
  //user context
  const { currentUser } = useContext(UserContext);
  const cancelToken = axios.CancelToken;
  const source = cancelToken.source();
  const ClassList = useGetItemClasses({ IsPurchasedMaterial: true });

  const [classFunctionList, setClassFunctionList] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [editCopy, setEditCopy] = useState(null);
  const [editIndex, setEditIndex] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [screenFields, setScreenFields] = useState({
    planningList: [],
    itemOrderList: [],
    payload: {
      abcCode: { value: 0, label: "---All---" },
      classCode: { value: "-1", label: "---All---" },
      vendor: null,
      itemNumberChecked: true,
      itemDescriptionPartialChecked: false,
      itemNumber: "",
      itemDescriptionPartial: "",
      sortDirection: "",
      sortField: "",
      orderRange: { label: "One Week", value: "7" },
      excludeInTransit: false,
    },
    page: {
      number: 1,
      size: 25,
      total: 0,
      itemOrderListPrestine: [],
    },
    sortDirection: "asc",
    sortField: "",
  });
  let DisplayedManagedItems = [];

  useEffect(() => {
    if (!currentUser) {
      setRoles([]);
    } else {
      let roleList = _.map(currentUser.roles, (r) => r.typeOfUserRole);
      setRoles(roleList);
    }
  }, [currentUser]);

  useEffect(() => {
    if (!ClassList.Success) return;
    const list = _.cloneDeep(ClassList.ClassList);
    if (_.isArray(list)) {
      list.unshift({ value: "-1", label: "---All---" });
      const orderedList = _.orderBy(list, (x) => x.label);
      setClassFunctionList(orderedList);
    } else {
      setClassFunctionList([]);
    }
  }, [ClassList]);

  function allowEdit(index) {
    if (screenFields.itemOrderList.length < 1) return;
    setEditIndex(index);
    setEditCopy(_.assign({}, screenFields.itemOrderList[index]));
  }

  function cancelEdit() {
    setEditIndex(null);
    setEditCopy(null);
    setIsLoading(false);
  }

  function CreatePayload() {
    const classCode = screenFields.payload.classCode.value;
    const abcCode = screenFields.payload.abcCode.value;
    const orderRange = screenFields.payload.orderRange.value;
    if (typeof classCode !== "string") {
      toast.error("incorrect classCode format");
    }
    if (typeof abcCode !== "number") {
      toast.error("incorrect abcCode format");
    }
    if (typeof orderRange !== "string")
      toast.error("incorrect orderangeFormat");
    let payload = Object.assign(
      {
        ItemDescription: screenFields.payload.itemDescriptionPartial,
        ItemNumber: screenFields.payload.itemNumber.value,
        ABCCode: abcCode,
        ClassCode: classCode,
        VendorId: screenFields.payload.vendor
          ? screenFields.payload.vendor.value
          : screenFields.payload.vendor,
        OrderRange: orderRange,
        ExcludeInTransit: screenFields.payload.excludeInTransit,
      },
      {}
    );
    return payload;
  }

  function handleEditChange(field, value) {
    const copyUpdate = _.assign({}, editCopy);
    copyUpdate[field] = value;
    setEditCopy(copyUpdate);
  }

  const LoadManagedItems = () => {
    setIsLoading(true);
    api
      .postS("Purchasing/RawMaterialItemInfo", CreatePayload(), source.token)
      .then((r) => {
        if (r.data?.success) {
          if (r.data.rawMaterialInfoList.length) {
            const copy = _.cloneDeep(screenFields);
            let data = r.data?.rawMaterialInfoList;
            if (data.length > 25) {
              PaginateList({
                number: 1,
                size: 25,
                itemOrderListPrestine: data,
              });
            } else {
              setScreenFields({
                ...copy,
                itemOrderList: data,
                planningList: [],
                page: {
                  itemOrderListPrestine: [],
                  number: 1,
                  size: 25,
                  total: 0,
                },
              });
            }
          } else {
            setScreenFields({
              ...screenFields,
              planningList: [],
              itemOrderList: [],
            });
          }
        } else {
          throw new Error(r);
        }
      })
      .catch(api.catchHandler)
      .finally(() => {
        setIsLoading(false);
      });
  };

  const LoadPlanningTable = () => {
    if (!ClassList.Success) return;
    setIsLoading(true);
    api
      .postS("Purchasing/RawMaterialPlaning", CreatePayload(), source.token)
      .then((response) => {
        if (response.data?.success) {
          if (response.data.rawMaterialPlanningList.length) {
            const copy = _.cloneDeep(screenFields);
            const data = response.data?.rawMaterialPlanningList;
            //count the total months
            setScreenFields({
              ...copy,
              planningList: data,
              itemOrderList: [],
            });
          } else {
            setScreenFields({
              ...screenFields,
              planningList: [],
              itemOrderList: [],
            });
          }
        } else {
          throw new Error(response);
        }
      })
      .catch(api.catchHandler)
      .finally(() => {
        setIsLoading(false);
      });
  };

  const onChange = (property, value, copy = null) => {
    if (copy === null) copy = _.cloneDeep(screenFields);
    _.set(copy, property, value);
    setScreenFields(copy);
    return;
  };

  const onExportToXlsx = () => {
    setDownloading(true);
    const payload = CreatePayload();

    api
      .postGetBlob("Purchasing/ExportPurchasedRawMaterialsXLSX", payload)
      .then((r) => {
        if (r.data.success === false) {
          if (_.isString(r.data.message)) {
            toast.error(r.data.message);
          }
          return;
        }
        if (r.data) {
          const XLSX_FILENAME = `PM Planning.xlsx`;
          helpers.downloadExport(
            r.data,
            XLSX_FILENAME,
            constants.MIME_TYPES.xlsx
          );
        } else {
          toast.error(r.message || "There was an error downloading the purchased materials report");
        }
      })
      .catch((error) => {
        api.catchHandler(error);
        toast.error(
          "There was an error downloading the purchased materials report"
        );
      })
      .finally(() => setDownloading(false));
  };

  const pageSpecificSorting = (state) => {
    const { sortField, sortDirection } = state;
    const data = _.orderBy(
      screenFields.planningList,
      [sortField, "orderQuantityNeeded"],
      [sortDirection, "desc"]
    );

    setScreenFields({
      ...screenFields,
      sortDirection: sortDirection,
      sortField: sortField,
      planningList: data,
    });
  };

  const PaginateList = ({ number = 1, size = 25, itemOrderListPrestine = [] }) => {
    const totalLength = itemOrderListPrestine.length;
    const copy = _.cloneDeep(screenFields);
    const Number = number;
    const Total = _.ceil(totalLength / size);
    const List = _.chain(itemOrderListPrestine)
      .drop(size * (number - 1))
      .take(size)
      .value();
    const Size = size;
    setScreenFields({
      ...copy,
      itemOrderList: List,
      planningList: [],
      page: {
        itemOrderListPrestine: itemOrderListPrestine,
        number: Number,
        size: Size,
        total: Total,
      },
    });
  };

  const RadioButtonTypeAhead = (
    currentValue,
    PropertyToBecomeTrue,
    PropertyToBecomeFalse,
    PropertyToBecomeNull
  ) => {
    if (currentValue) return; // no need to switch it is already true
    const copy = _.cloneDeep(screenFields);
    _.set(copy, PropertyToBecomeTrue, true);
    _.set(copy, PropertyToBecomeFalse, false);
    _.set(copy, PropertyToBecomeNull, "");
    setScreenFields(copy);
  };

  function updateItemDesc() {
    const data = _.cloneDeep(screenFields.itemOrderList);

    const payload = _.chain({})
      .assign(editCopy)
      .pickBy((_v, k) => editCopy[k] !== data[editIndex][k])
      .value();

    if (_.keys(payload).length) {
      payload["itemNumber"] = editCopy.itemNumber;
      payload["siteId"] = editCopy.siteId;
      payload["orderPointQty"] = editCopy.orderPoint;
      if (
        !payload.itemNumber.length ||
        !payload.siteId.length ||
        payload.orderPointQty === undefined
      ) {
        toast.error("Updating Order Point has failed, please try again");
      }
      setIsLoading(true);
      api
        .post(`Inventory/UpdateGPOrderPointAndQty`, payload)
        .then((response) => {
          if (response.data.success) {
            LoadManagedItems();
          } else {
            if (_.isString(response?.data?.message)) {
              toast.error(response?.data?.message);
            }
          }
        })
        .catch(api.catchHandler)
        .finally(() => {
          cancelEdit();
        })
        .finally(() => setIsLoading(false));
    } else {
      cancelEdit();
    }
  }

  return (
    (document.title = `Purchase Order Planning`),
    (
      <DesktopContainer screenName={"Purchase Order Planning"}>
        <div className="filter-header stick-top mb-3">
          <Row className="mt-3">
            <Col xs="3" style={{ textAlign: "right", marginTop: "6px", color: "white" }}>
              Order Range:
            </Col>
            <Col xs="6">
              <Select
                classNamePrefix="serviceThreadSelect"
                options={ClassList.OrderRangeList}
                value={screenFields.payload.orderRange}
                onChange={(opt) => onChange("payload.orderRange", opt)}
              // isMulti={true}
              // isClearable={true}
              />
            </Col>
          </Row>

          <Row xs="" className="mt-3">
            <Col xs="3" style={{ textAlign: "right", marginTop: "6px", color: "white" }}>
              Class:
            </Col>
            <Col xs="6">
              <Select
                classNamePrefix="serviceThreadSelect"
                options={classFunctionList}
                // options={ClassListFunction}
                // options={ClassList.ClassList}
                // options={ClassList.ClassList?.unshift({ value: "-1", label: "---All---"})}
                value={screenFields.payload.classCode}
                onChange={(opt) => onChange("payload.classCode", opt)}
              // isMulti={true}
              // isClearable={true}
              />
            </Col>
          </Row>

          <Row xs="" className="mt-3">
            <Col xs="3" style={{ textAlign: "right", marginTop: "6px", color: "white" }}>
              ABC Code:
            </Col>
            <Col xs="6">
              <Select
                classNamePrefix="serviceThreadSelect"
                options={ClassList.ABCCodeList}
                value={screenFields.payload.abcCode}
                onChange={(opt) => onChange("payload.abcCode", opt)}
              // isMulti={true}
              // isClearable={true}
              />
            </Col>
          </Row>

          <Row xs="" className="mt-3">
            <Col xs="3" style={{ textAlign: "right", marginTop: "6px", color: "white" }}>
              Vendor:
            </Col>
            <Col xs="6">
              <VendorTypeahead
                value={screenFields.payload.vendor}
                onChange={(opt) => onChange("payload.vendor", opt)}
              />
            </Col>
          </Row>

          <Row className="mt-3">
            <Col xs="3" style={{ textAlign: "right", marginRight: "5px", color: "white" }}>
              Item Number:
            </Col>
            <Col xs="1" style={{ paddingRight: "0px" }}>
              <Input
                type="checkbox"
                name={"ItemNumber"}
                checked={screenFields.payload.itemNumberChecked}
                onChange={() =>
                  RadioButtonTypeAhead(
                    screenFields.payload.itemNumberChecked,
                    "payload.itemNumberChecked",
                    "payload.itemDescriptionPartialChecked",
                    "payload.itemDescriptionPartial"
                  )
                }
              />
            </Col>
            <Col xs="4">
              <ItemTypeahead
                value={screenFields.payload.itemNumber}
                onChange={(opt) => onChange("payload.itemNumber", opt)}
                disabled={screenFields.payload.itemDescriptionPartialChecked}
              />
            </Col>
            <Col xs="4"></Col>
          </Row>

          <Row className="mt-2">
            <Col xs="3" style={{ textAlign: "right", marginRight: "5px", color: "white" }}>
              Item Description Partial:
            </Col>
            <Col xs="1" style={{ paddingRight: "0px" }}>
              <Input
                type="checkbox"
                name={"ItemDescriptionPartial"}
                checked={screenFields.payload.itemDescriptionPartialChecked}
                onChange={() =>
                  RadioButtonTypeAhead(
                    screenFields.payload.itemDescriptionPartialChecked,
                    "payload.itemDescriptionPartialChecked",
                    "payload.itemNumberChecked",
                    "payload.itemNumber"
                  )
                }
              />
            </Col>
            {/* { screenFields.payload.itemDescriptionPartialChecked &&  */}
            <Col xs="4">
              <Input
                type="text"
                name={"ItemDescriptionPartial"}
                value={screenFields.payload.itemDescriptionPartial}
                disabled={screenFields.payload.itemNumberChecked}
                onChange={(e) => { onChange("payload.itemDescriptionPartial", e.target.value); }}
              />
            </Col>
            {/* } */}
          </Row>
          <Row className="mt-2">
            <Col xs="3" style={{ textAlign: "right", marginRight: "5px", color: "white" }} >
              Exclude In Transit Amounts from Qty On Hand:
            </Col>
            <Col xs="1" style={{ paddingRight: "0px" }}>
              <Input
                type="checkbox"
                name={"ExcludeInTransit"}
                checked={screenFields.payload.excludeInTransit}
                onChange={() => onChange("payload.excludeInTransit", !screenFields.payload.excludeInTransit)}
              />
            </Col>
          </Row>
          <Row className="mt-3">
            <Col>
              <Row>
                <Col xs="3"></Col>
                <Col>
                  <Button
                    className="shredButton"
                    style={{ marginRight: "15px" }}
                    onClick={LoadPlanningTable}
                    disabled={isLoading}
                  >
                    SHOW PURCHASE ORDER PLANNING
                  </Button>
                  <Button
                    className="shredButton"
                    onClick={LoadManagedItems}
                    disabled={isLoading}
                  >
                    MANAGE ITEM ORDER POINTS
                  </Button>
                </Col>
                <Col xs="3"></Col>
              </Row>
            </Col>
          </Row>
        </div>
        <Card className="dashboard-card">
          <CardBody>
            <ServiceThreadSpinner on={isLoading}>
              {screenFields && screenFields.planningList?.length !== 0 && (
                <>
                  <Row className="align-items-center mb-2">
                    <Col className="transparent-background">
                      <ButtonGroup className="float-end">
                        <Button
                          title="Download as .xlsx"
                          className="shredButtonExcel float-end ms-2"
                          onClick={onExportToXlsx}
                          disabled={downloading}
                        >
                          <FontAwesomeIcon icon="file-excel" size="lg" />
                        </Button>
                      </ButtonGroup>
                    </Col>
                  </Row>
                  <Table size="sm" className="data-table">
                    <thead className="table-header">
                      <tr>
                        <th>
                          <PageSpecificSortHeader
                            displayName={"Item"}
                            field={"itemNumber"}
                            sortDirection={screenFields.sortDirection}
                            sorted={screenFields.sortField === "itemNumber"}
                            callback={pageSpecificSorting}
                            state={{
                              sortField: "itemNumber",
                              sortDirection:
                                screenFields.sortDirection === "asc"
                                  ? "desc"
                                  : "asc",
                            }}
                          />
                        </th>
                        <th>
                          <PageSpecificSortHeader
                            displayName={"Class"}
                            field={"itemClass"}
                            sortDirection={screenFields.sortDirection}
                            sorted={screenFields.sortField === "itemClass"}
                            callback={pageSpecificSorting}
                            state={{
                              sortField: "itemClass",
                              sortDirection:
                                screenFields.sortDirection === "asc"
                                  ? "desc"
                                  : "asc",
                            }}
                          />
                        </th>
                        <th>
                          <PageSpecificSortHeader
                            displayName={
                              <span>ABC<br />Code</span>
                            }
                            field={"abcDisplayCode"}
                            sortDirection={screenFields.sortDirection}
                            sorted={screenFields.sortField === "abcCode"}
                            callback={pageSpecificSorting}
                            state={{
                              sortField: "abcCode",
                              sortDirection:
                                screenFields.sortDirection === "asc"
                                  ? "desc"
                                  : "asc",
                            }}
                          />
                        </th>
                        <th>
                          <PageSpecificSortHeader
                            displayName={"Vendor"}
                            field={"vendorNumber"}
                            sortDirection={screenFields.sortDirection}
                            sorted={screenFields.sortField === "vendorNumber"}
                            callback={pageSpecificSorting}
                            state={{
                              sortField: "vendorNumber",
                              sortDirection:
                                screenFields.sortDirection === "asc"
                                  ? "desc"
                                  : "asc",
                            }}
                          />
                        </th>
                        <th>
                          <PageSpecificSortHeader
                            displayName={
                              <span>Add<br />Qty<br />Needs</span>
                            }
                            field={"orderQuantityNeeded"}
                            sortDirection={screenFields.sortDirection}
                            sorted={screenFields.sortField === "orderQuantityNeeded"}
                            callback={pageSpecificSorting}
                            state={{
                              sortField: "orderQuantityNeeded",
                              sortDirection:
                                screenFields.sortDirection === "asc"
                                  ? "desc"
                                  : "asc",
                            }}
                          />
                        </th>
                        <th>
                          <PageSpecificSortHeader
                            displayName={"On Hand"}
                            field={"rawTotalOnHand"}
                            sortDirection={screenFields.sortDirection}
                            sorted={screenFields.sortField === "rawTotalOnHand"}
                            callback={pageSpecificSorting}
                            state={{
                              sortField: "rawTotalOnHand",
                              sortDirection:
                                screenFields.sortDirection === "asc"
                                  ? "desc"
                                  : "asc",
                            }}
                          />
                        </th>
                        <th>
                          <PageSpecificSortHeader
                            displayName={"On POs"}
                            field={"rawTotalOrdered"}
                            sortDirection={screenFields.sortDirection}
                            sorted={screenFields.sortField === "rawTotalOrdered"}
                            callback={pageSpecificSorting}
                            state={{
                              sortField: "rawTotalOrdered",
                              sortDirection:
                                screenFields.sortDirection === "asc"
                                  ? "desc"
                                  : "asc",
                            }}
                          />
                        </th>
                        <th>
                          <PageSpecificSortHeader
                            displayName={"PLT"}
                            field={"pltAmount"}
                            sortDirection={screenFields.sortDirection}
                            sorted={screenFields.sortField === "pltAmount"}
                            callback={pageSpecificSorting}
                            state={{
                              sortField: "pltAmount",
                              sortDirection:
                                screenFields.sortDirection === "asc"
                                  ? "desc"
                                  : "asc",
                            }}
                          />
                        </th>
                        <th>
                          <PageSpecificSortHeader
                            displayName={"PLTWHSE"}
                            field={"pltWhseAmount"}
                            sortDirection={screenFields.sortDirection}
                            sorted={screenFields.sortField === "pltWhseAmount"}
                            callback={pageSpecificSorting}
                            state={{
                              sortField: "pltWhseAmount",
                              sortDirection:
                                screenFields.sortDirection === "asc"
                                  ? "desc"
                                  : "asc",
                            }}
                          />
                        </th>
                        <th>
                          <PageSpecificSortHeader
                            displayName={"In Transit"}
                            field={"intransitAmount"}
                            sortDirection={screenFields.sortDirection}
                            sorted={screenFields.sortField === "intransitAmount"}
                            callback={pageSpecificSorting}
                            state={{
                              sortField: "intransitAmount",
                              sortDirection:
                                screenFields.sortDirection === "asc"
                                  ? "desc"
                                  : "asc",
                            }}
                          />
                        </th>
                        <th>
                          <PageSpecificSortHeader
                            displayName={"FG Needs"}
                            field={"productionQuantityNeeded"}
                            sortDirection={screenFields.sortDirection}
                            sorted={screenFields.sortField === "productionQuantityNeeded"}
                            callback={pageSpecificSorting}
                            state={{
                              sortField: "productionQuantityNeeded",
                              sortDirection:
                                screenFields.sortDirection === "asc"
                                  ? "desc"
                                  : "asc",
                            }}
                          />
                        </th>
                        <th>
                          <PageSpecificSortHeader
                            displayName={"Order Point"}
                            field={"rawTotalOrderPoint"}
                            sortDirection={screenFields.sortDirection}
                            sorted={screenFields.sortField === "rawTotalOrderPoint"}
                            callback={pageSpecificSorting}
                            state={{
                              sortField: "rawTotalOrderPoint",
                              sortDirection:
                                screenFields.sortDirection === "asc"
                                  ? "desc"
                                  : "asc",
                            }}
                          />
                        </th>
                        <th>
                          <PageSpecificSortHeader
                            displayName={"Fixed Order Qty"}
                            field={"rawTotalFixedOrderQty"}
                            sortDirection={screenFields.sortDirection}
                            sorted={screenFields.sortField === "rawTotalFixedOrderQty"}
                            callback={pageSpecificSorting}
                            state={{
                              sortField: "rawTotalFixedOrderQty",
                              sortDirection:
                                screenFields.sortDirection === "asc"
                                  ? "desc"
                                  : "asc",
                            }}
                          />
                        </th>
                        <th>
                          <PageSpecificSortHeader
                            displayName={"Lead Time"}
                            field={"planningLeadTime"}
                            sortDirection={screenFields.sortDirection}
                            sorted={screenFields.sortField === "planningLeadTime"}
                            callback={pageSpecificSorting}
                            state={{
                              sortField: "planningLeadTime",
                              sortDirection:
                                screenFields.sortDirection === "asc"
                                  ? "desc"
                                  : "asc",
                            }}
                          />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {_.map(screenFields.planningList, (datum, index) => {
                        return (
                          <tr key={index}
                            className={`tr-${index % 2 === 0 ? "primary" : "secondary"}`}
                          >
                            <ItemDropdownMenu
                              itemNumber={datum.itemNumber}
                              itemDescription={_.trim(datum.itemWithDesc)}
                              attachmentCount={datum.attachmentCount}
                            />
                            <td>{datum.itemClass}</td>
                            <td>{datum.abcDisplayCode}</td>
                            <td>{datum.vendorNumber}</td>
                            <td>{Math.round(datum.orderQuantityNeeded).toLocaleString()}</td>
                            <td>{Math.round(datum.rawTotalOnHand).toLocaleString()}</td>
                            {datum.rawTotalOrdered === 0 ? (
                              <td>{Math.round(datum.rawTotalOrdered).toLocaleString()}</td>
                            ) : (
                              <PORawMaterialDropDown
                                itemNumber={datum.itemNumber}
                                rawTotalOrdered={datum.rawTotalOrdered}
                              />
                            )}
                            <td>{Math.round(datum.pltAmount).toLocaleString()}</td>
                            <td>{Math.round(datum.pltWhseAmount).toLocaleString()}</td>
                            <td>{Math.round(datum.intransitAmount).toLocaleString()}</td>
                            <td>{Math.round(datum.productionQuantityNeeded).toLocaleString()}</td>
                            <td>{Math.round(datum.rawTotalOrderPoint).toLocaleString()}</td>
                            {/*pltAmount*/}
                            <td>{Math.round(datum.rawTotalFixedOrderQty).toLocaleString()}</td>
                            <td>{Math.round(datum.planningLeadTime).toLocaleString()}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </>
              )}
              {screenFields &&
                screenFields.itemOrderList.length !== 0 &&
                roles.length !== 0 && (
                  <>
                    <Table size="sm" className="data-table">
                      <thead className="table-header">
                        <tr>
                          <th>Item</th>
                          <th>Class</th>
                          <th>
                            <span>ABC<br />Code</span>
                          </th>
                          <th>Vendor</th>
                          <th>Site</th>
                          <th>On Order</th>
                          <th>
                            <span>On<br />Hand</span>
                          </th>
                          <th>Allocated</th>
                          <th>Available</th>
                          <th>Order Point</th>
                          <th>Fixed Order<br />Qty</th>
                          {helpers.hasRole(roles, [
                            constants.ROLE_IDS.Admin,
                            constants.ROLE_IDS.PowerUser,
                            constants.ROLE_IDS.PurchasingEdit,
                          ]) ? (
                            <th></th>
                          ) : null}
                        </tr>
                      </thead>
                      <tbody>
                        {_.map(screenFields.itemOrderList, (datum, index) => {
                          const isEditing = editIndex === index;
                          const readOnly = !isEditing;
                          const alreadyDisplayed = _.includes(
                            DisplayedManagedItems,
                            datum.itemNumber
                          );
                          DisplayedManagedItems.push(datum.itemNumber);
                          return (
                            <tr
                              key={index}
                              className={`tr-${index % 2 === 0 ? "primary" : "secondary"
                                }`}
                            >
                              {alreadyDisplayed ? (
                                <td>{""}</td>
                              ) : (
                                <ItemDropdownMenu
                                  itemNumber={datum.itemNumber}
                                  itemDescription={_.trim(datum.itemWithDescription)}
                                  attachmentCount={datum.attachmentCount}
                                />
                              )}
                              {alreadyDisplayed ? (
                                <td>{""}</td>
                              ) : (
                                <td>{datum.itemClass}</td>
                              )}
                              {alreadyDisplayed ? (
                                <td>{""}</td>
                              ) : (
                                <td>{datum.abcValue}</td>
                              )}
                              {alreadyDisplayed ? (
                                <td>{""}</td>
                              ) : (
                                <td>{datum.vendorId}</td>
                              )}
                              <td>{datum.siteId}</td>
                              <td>{Math.round(datum.onOrder).toLocaleString()}</td>{" "}
                              {/*orderQuantityNeeded*/}
                              <td>{Math.round(datum.onHand).toLocaleString()}</td>
                              <td>{Math.round(datum.allocated).toLocaleString()}</td>
                              <td>{Math.round(datum.available).toLocaleString()}</td>
                              {isEditing ? (
                                <td>
                                  <NumberFormat
                                    className="form-control"
                                    value={editCopy.orderPoint}
                                    decimalScale={3}
                                    fixedDecimalScale
                                    allowNegative={false}
                                    onValueChange={({ floatValue }) => {
                                      handleEditChange("orderPoint", floatValue);
                                    }}
                                  />
                                </td>
                              ) : (
                                <td>
                                  {Math.round(datum.orderPoint).toLocaleString()}
                                </td>
                              )}
                              {isEditing ? (
                                <td>
                                  <NumberFormat
                                    className="form-control"
                                    value={editCopy.fixedOrderQty}
                                    decimalScale={3}
                                    fixedDecimalScale
                                    allowNegative={false}
                                    onValueChange={({ floatValue }) => {
                                      handleEditChange("fixedOrderQty", floatValue);
                                    }}
                                  />
                                </td>
                              ) : (
                                <td>
                                  {Math.round(datum.fixedOrderQty).toLocaleString()}
                                </td>
                              )}
                              {helpers.hasRole(roles, [
                                constants.ROLE_IDS.Admin,
                                constants.ROLE_IDS.PowerUser,
                                constants.ROLE_IDS.PurchasingEdit,
                              ]) ? (
                                <td className="data-cell">
                                  <ButtonGroup className="width95px">
                                    <Button
                                      className={`icon-button me-1 ${isEditing ? "" : "float-end"}`}
                                      title={isEditing ? "Save changes" : "Edit line"}
                                      color={isEditing ? "success" : "info"}
                                      onClick={
                                        isEditing
                                          ? updateItemDesc
                                          : () => allowEdit(index)
                                      }
                                      disabled={
                                        (editIndex !== null && readOnly) ||
                                        // validateEditCopy() ||
                                        isLoading
                                      }
                                    >
                                      <FontAwesomeIcon className="whiteIcon" icon={isEditing ? "check" : "edit"}/>
                                    </Button>
                                    <Button
                                      className={`icon-button ${isEditing ? "" : "vis-hidden"}`}
                                      color="secondary"
                                      title="Discard changes"
                                      onClick={cancelEdit}
                                      disabled={editIndex !== null && readOnly}
                                    >
                                      <FontAwesomeIcon className="whiteIcon" icon="times"/>
                                    </Button>
                                  </ButtonGroup>
                                </td>
                              ) : null}
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                    <Pager
                      callBack={(number) => PaginateList({ ...screenFields?.page, number: number })}
                      sizeCallback={(size) => PaginateList({ ...screenFields?.page, size: size })}
                      pageNumber={screenFields.page?.number}
                      pageSize={screenFields.page?.size}
                      totalPages={screenFields.page?.total}
                      className={"float-start justify-content-center"}
                    />
                  </>
                )}
              {screenFields &&
                screenFields.itemOrderList.length < 1 &&
                screenFields.planningList.length < 1 && (
                  <div className="mt-5" style={{ textAlign: "center" }}>
                    <span>Results Not Found</span>
                  </div>
                )}
            </ServiceThreadSpinner>
          </CardBody>
        </Card>
      </DesktopContainer>
    )
  );
}

function PageSpecificSortHeader(props) {
  const [direction, setDirection] = useState(props.sortDirection || "asc");

  useEffect(() => {
    setDirection(props.sortDirection || "asc");
  }, [props.sortDirection]);

  const callBackDir = direction === "desc" ? "asc" : "desc";

  return (
    <span
      className="sort-handle"
      onClick={() =>
        props.state
          ? props.callback(props.state)
          : props.callback(props.field, callBackDir)
      }
    >
      {props.displayName}
      {props.sorted && direction === "asc" && (
        <FontAwesomeIcon icon="angle-up" className="ms-1" />
      )}
      {props.sorted && direction === "desc" && (
        <FontAwesomeIcon icon="angle-down" className="ms-1" />
      )}
    </span>
  );
}
